/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useMemo, useRef } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import GenericButton from "@ui-components/Button/GenericButton";
import { ReactComponent as IconPlus } from "@shared/assets/Icons/IconPlusV3.svg";
import { clusterColumns, Cluster } from "./ClusterData";
import { getClusters } from "./request/request";
import ClusterModal from "./ClusterModal/ClusterModal";
import { useAuth0 } from "@auth0/auth0-react";
import { filterClusters } from "@shared/components/utils/searchUtils";
import { useAppSelector } from "@shared/redux/hooks";
import { DataStateView } from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { ReflectorManagementOnboardingEmptyState } from "@shared/components/EmptyItems";
import { getTableBodyMaxHeight, REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS } from "../../../helpers/tableViewport";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { isTenantScopeForbidden } from "@shared/helpers/tenantScope";
import { RefreshingIndicator } from "@shared/components/ListLoading";
import { clusterListCacheKey } from "../../../helpers/cacheKeys";

const suggestedKeys = [
  "Cluster ID",
  "Name",
  "Status",
  "Cloud",
  "Region",
  "Description",
  "Reflectors",
  "Last Sync",
];

const NO_CLUSTERS: Cluster[] = [];
const INITIAL_ROWS_PER_PAGE = 10;

const ClusterPage: React.FC = () => {
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [isClusterModalOpen, setIsClusterModalOpen] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  // Stable deps: no getAccessTokenSilently, so a resize does not refetch.
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;

	const { tenantId } = useAppSelector(state => state.selection);

  // Cached per tenant: coming back to the list paints the last answer at once and
  // refreshes it underneath.
  const {
    data: clusters = NO_CLUSTERS,
    error,
    isLoading,
    isRefreshing,
    reload: reloadClusters,
  } = useCachedFetch(tenantId ? clusterListCacheKey(tenantId) : null, async (): Promise<Cluster[]> => {
    try {
      const token = await getAccessTokenSilentlyRef.current();
      const res = await getClusters(token, tenantId);
      return res.data.map((cluster: any) => ({
        ...cluster,
        reflectors: Array.isArray(cluster.instances)
          ? cluster.instances.map((inst: any) => inst.name)
          : [],
        status: cluster.status || "Unknown",
      }));
    } catch (err) {
      // A tenant-scope refusal goes up as is: the hook drops this tenant's rows (CTX-866).
      if (isTenantScopeForbidden(err)) throw err;
      throw new Error("Failed to load clusters.");
    }
  });

  // Filtering logic using utility function
  const filteredData = useMemo(() => {
    return filterClusters(clusters, activeFilters);
  }, [activeFilters, clusters]);

  const handleCreateCluster = async (data: any) => {
    // Don't close modal immediately - let the alert show first
    // The modal will close when the user dismisses the alert
    // Wait a bit for the backend to process the creation, then refetch: the new cluster
    // lands in the table and in the cache when the refresh answers.
    await new Promise(resolve => setTimeout(resolve, 1000));
    reloadClusters();
    setIsClusterModalOpen(false);
  };

  return (
    <div className="relative">
      <RefreshingIndicator active={isRefreshing} />
      <GenericCard isCollapsible={false}>
        {/* Searchbar and Create Cluster button (button below searchbar) */}
        <div className="mb-2 p-4">
          <div className="flex justify-end mb-4">
            <GenericButton
              label="Create Cluster"
              variant="brand"
              size="md"
              icon={<IconPlus className="w-5 h-5" />}
              iconPosition="left"
              onClick={() => setIsClusterModalOpen(true)}
            />
          </div>
          <GenericSearchbar
            placeholder="Find by attribute or value (case-sensitive)"
            suggestedKeys={suggestedKeys}
            onFiltersChange={setActiveFilters}
          />
        </div>
        <DataStateView
          loading={isLoading}
          error={error}
          isEmpty={!isLoading && !error && clusters.length === 0}
          onRetry={reloadClusters}
          emptyContent={
            <ReflectorManagementOnboardingEmptyState heading="You have no Clusters yet" />
          }
          loadingContent={
            <TableSkeleton
              columns={clusterColumns.length}
              rows={8}
              className="mx-4 mb-4"
            />
          }
        >
          <GenericTable
            columns={clusterColumns}
            data={filteredData}
            initialRowsPerPage={INITIAL_ROWS_PER_PAGE}
            rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
            maxTableBodyHeight={getTableBodyMaxHeight(320)}
          />
        </DataStateView>
      </GenericCard>
      <ClusterModal
        isOpen={isClusterModalOpen}
        onClose={() => setIsClusterModalOpen(false)}
        onSubmit={handleCreateCluster}
      />
    </div>
  );
};

export default ClusterPage;
